/**
 * useClubInstructors — instructors bookable for a DIRECT lesson at this club.
 * [BUT-843, ours]
 *
 * Shape follows `bayHooks.ts`'s `useAvailableBaysQuery`: a typed params
 * interface with `enabled?: boolean` last, a `queryKey` ternary that never
 * keys on `0`, a `queryFn` that does NOT unwrap the envelope, a cache
 * preset spread, and a return that spreads the whole query result plus
 * envelope-aware convenience fields.
 *
 * This is the fallback SOURCE when the member holds no pack — a pack holder
 * is offered `pack.allowedInstructors` instead, never this list. A
 * failed packs query must never fall back to this hook's result — that
 * decision is made by the caller, which is why the two hooks stay separate
 * rather than merged into one "instructors for step 0" hook.
 *
 * @module lessons/hooks/useClubInstructors
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { ApiErrorResult, ApiResult } from '~/lib/apiClient';
import { queryKeys } from '~/lib/query/keys';
import { cachePresets } from '~/lib/query/cachePresets';
import { appRuntime } from '~/services/appRuntime';
import { listClubInstructors } from '../api/listClubInstructors';
import type { ClubInstructor } from '../types';

/** Module scope — react-state.md rule 2: a hook must not return a freshly-allocated array. */
const NO_INSTRUCTORS: ClubInstructor[] = [];

export interface UseClubInstructorsParams {
  clubId: number | undefined;
  enabled?: boolean;
}

type ClubInstructorsResult = ApiResult<{ instructors: ClubInstructor[] }>;

export type UseClubInstructorsResult = UseQueryResult<ClubInstructorsResult, Error> & {
  /** The latest envelope, or null before the first answer. */
  result: ClubInstructorsResult | null;
  /** The club's bookable roster. Never undefined. */
  instructors: ClubInstructor[];
  /** An answer arrived at least once. */
  hasAnswer: boolean;
  /** The refusal envelope when the latest answer is `success: false`. */
  failure: ApiErrorResult | null;
};

/**
 * A club's bookable instructor roster.
 *
 * `cachePresets.stable` (5 min): a club's bookable roster changes on admin
 * action, not minute-to-minute.
 */
export function useClubInstructors({
  clubId,
  enabled = true,
}: UseClubInstructorsParams): UseClubInstructorsResult {
  const tenantHandle = appRuntime.tenantHandle ?? undefined;

  const query = useQuery({
    queryKey: clubId ? queryKeys.lessons.instructors(clubId) : queryKeys.disabled.lessons(),
    queryFn: () => {
      if (!clubId || !tenantHandle) throw new Error('clubId and tenantHandle are required');
      return listClubInstructors({ clubId, tenantHandle });
    },
    enabled: enabled && !!clubId && !!tenantHandle,
    ...cachePresets.stable,
  });

  const result = query.data;

  return {
    ...query,
    result: result ?? null,
    instructors: result?.success ? result.payload.instructors : NO_INSTRUCTORS,
    hasAnswer: result !== undefined,
    failure: result && !result.success ? result : null,
  };
}
